import * as oc from 'openid-client';

import {
  NONCE_KEY,
  PKCE_VERIFIER_KEY,
  REACT_APP_ENVIRONMENT,
  REACT_APP_CUCON_OIDC_DOMAIN,
  SIGNIN_BACK_PATH,
  STATE_KEY,
} from '@/config';
import { CUCON_OIDC_API_PATH } from '@/constants';
import { serviceProfile as jsonServiceProfile } from '@/profile/serviceProfile';
import { CUCON_API_KEY_NAME, getToken } from '@/utils/storage';

// const apiKey = jsonServiceProfile?.cuconConfig?.apiKey ?? '';

const decodeJWT = (
  token: string
): {
  client_id: string;
  sub: string;
  exp: number;
  canal: { tenant?: string };
} | null => {
  const payloadBase64 = token.split('.')[1];
  if (payloadBase64 == null || payloadBase64 === '') {
    return null;
  }

  try {
    const decodedPayload = atob(
      payloadBase64.replace(/-/g, '+').replace(/_/g, '/')
    );

    return JSON.parse(decodedPayload);
  } catch {
    return null;
  }
};

// TODO 現状、APIキーからテナントIDを取得しているが、将来的にテナントエイリアスでアクセス可能になる想定
export function getTenantId(apiKey: string): string {
  const payload = decodeJWT(apiKey);

  return payload?.canal.tenant ?? '';
}

const getConfiguredApiKey = (): string =>
  jsonServiceProfile?.cuconConfig?.apiKey ?? getToken(CUCON_API_KEY_NAME) ?? '';

export const isMapFavoritesResourceServerEnabled = (): boolean => {
  return (
    REACT_APP_ENVIRONMENT === 'development' &&
    jsonServiceProfile?.cuconConfig?.tenantAlias === 'sano'
  );
};

const getResourceIndicator = (): string => {
  const tenantId = getTenantId(getConfiguredApiKey());

  if (tenantId === '') {
    throw new Error('Tenant ID is required to build the resource indicator.');
  }

  return `urn:uhuru:cucon-rs:map:${tenantId}`;
};

const CUCON_MAP_RS_SCOPES =
  'map_favorites:read map_favorites:write map_favorites:delete';
const getOidcScope = (): string => {
  if (isMapFavoritesResourceServerEnabled()) {
    return `openid profile email phone ${CUCON_MAP_RS_SCOPES}`;
  }

  return 'openid profile email phone';
};
const RESOURCE_ACCESS_TOKEN_KEY = 'cucon_map_rs_access_token';
const ID_TOKEN_KEY = 'cucon_oidc_id_token';
const OIDC_REDIRECT_PATH = '/get-token';

const getRedirectUri = (): URL => {
  return new URL(window.location.origin + OIDC_REDIRECT_PATH);
};

let _expiresInSeconds = 0;
let _userId = '';
let _name = '';
let _username = '';

const getCurrentUnixTimestamp = (): number => Math.floor(Date.now() / 1000);

const getLatestRefreshToken = (
  primaryRefreshToken: string | null | undefined,
  fallbackRefreshToken: string
): string => {
  if (primaryRefreshToken != null && primaryRefreshToken !== '') {
    return primaryRefreshToken;
  }

  return fallbackRefreshToken;
};

const isTokenValid = (): boolean => {
  const currentTime = getCurrentUnixTimestamp();

  return _expiresInSeconds > currentTime; // 有効期限をチェック
};

const refreshAccessToken = async (
  refreshToken: string
): Promise<{
  accessToken: string | null;
  refreshToken: string | null;
  userId: string;
  name: string;
  username: string;
} | null> => {
  const issuer = new URL(
    REACT_APP_CUCON_OIDC_DOMAIN + CUCON_OIDC_API_PATH.issuer
  );
  const clientId = jsonServiceProfile?.cuconConfig?.clientId ?? '';

  try {
    const config = await oc.discovery(issuer, clientId);
    const userInfoTokens = await oc.refreshTokenGrant(config, refreshToken);
    const claims = userInfoTokens.claims();
    const userInfoAccessToken = userInfoTokens.access_token ?? '';

    if (userInfoAccessToken === '') {
      throw new Error('Failed to refresh userinfo access token.');
    }

    const userinfo = await oc.fetchUserInfo(
      config,
      userInfoAccessToken,
      claims?.sub ?? ''
    );

    let resolvedLatestRefreshToken = getLatestRefreshToken(
      userInfoTokens.refresh_token,
      refreshToken
    );

    if (isMapFavoritesResourceServerEnabled()) {
      const rsTokens = await oc.refreshTokenGrant(
        config,
        userInfoTokens.refresh_token ?? refreshToken,
        {
          resource: getResourceIndicator(),
          scope: getOidcScope(),
        }
      );
      const resourceAccessToken = rsTokens.access_token ?? '';

      if (resourceAccessToken === '') {
        throw new Error('Failed to refresh resource access token.');
      }

      sessionStorage.setItem(RESOURCE_ACCESS_TOKEN_KEY, resourceAccessToken);
      resolvedLatestRefreshToken = getLatestRefreshToken(
        rsTokens.refresh_token,
        resolvedLatestRefreshToken
      );
    } else {
      sessionStorage.removeItem(RESOURCE_ACCESS_TOKEN_KEY);
    }

    // アプリ全体では従来どおり UserInfo 用 access token を保持する
    _expiresInSeconds =
      getCurrentUnixTimestamp() + (userInfoTokens.expires_in ?? 0);
    _userId = userinfo.sub ?? '';
    _name = userinfo.name ?? '';
    _username = (userinfo.username as string) ?? '';

    return {
      accessToken: userInfoAccessToken,
      refreshToken: resolvedLatestRefreshToken,
      userId: _userId,
      name: _name,
      username: _username,
    };
  } catch (reason) {
    console.error(reason);
  }

  return null;
};

// TODO 現状は、CUCONへのアクセス回数を減らすため、トークンに含まれるextで有効期限を判断する
export const checkAndRefreshToken = async (
  accessToken: string,
  refreshToken: string
): Promise<{
  accessToken: string | null;
  refreshToken: string | null;
  userId: string;
  name: string;
  username: string;
} | null> => {
  if (isTokenValid() && _userId !== '' && _name !== '' && _username !== '') {
    return {
      accessToken,
      refreshToken,
      userId: _userId,
      name: _name,
      username: _username,
    };
  } else {
    if (_expiresInSeconds !== 0) console.log('Token expired. Refreshing...');
    const newTokens = await refreshAccessToken(refreshToken);

    return newTokens;
  }
};

export const getOidcTokenAndUserInfo = async (): Promise<{
  accessToken: string | null;
  refreshToken: string | null;
  userId: string;
  name: string;
  username: string;
} | null> => {
  const redirectUri = getRedirectUri();
  const issuer = new URL(
    REACT_APP_CUCON_OIDC_DOMAIN + CUCON_OIDC_API_PATH.issuer
  );
  const clientId = jsonServiceProfile?.cuconConfig?.clientId ?? '';
  const codeVerifier = sessionStorage.getItem(PKCE_VERIFIER_KEY) ?? undefined;
  const expectedState = sessionStorage.getItem(STATE_KEY) ?? undefined;
  const expectedNonce = sessionStorage.getItem(NONCE_KEY) ?? undefined;

  const callbackUrl = new URL(window.location.href);
  const callbackError = callbackUrl.searchParams.get('error');
  const callbackErrorDescription =
    callbackUrl.searchParams.get('error_description');

  if (callbackError !== null) {
    console.error('OIDC callback error response', {
      callbackUrl: callbackUrl.toString(),
      callbackError,
      callbackErrorDescription,
      redirectUri: redirectUri.toString(),
    });

    return null;
  }

  try {
    const config = await oc.discovery(issuer, clientId);
    const tokens = await oc.authorizationCodeGrant(
      config,
      callbackUrl,
      {
        pkceCodeVerifier: codeVerifier,
        expectedState,
        expectedNonce,
      },
      {
        redirect_uri: redirectUri.toString(), // RFC 8707、token endpoint parameters の追加プロパティ
      }
    );

    if (tokens.refresh_token == null || tokens.refresh_token === '') {
      throw new Error('refresh_token was not issued.');
    }

    const claims = tokens.claims();
    if (tokens.id_token != null) {
      sessionStorage.setItem(ID_TOKEN_KEY, tokens.id_token);
    }
    const userinfo = await oc.fetchUserInfo(
      config,
      tokens.access_token,
      claims?.sub ?? ''
    );
    _userId = userinfo.sub ?? '';
    _name = userinfo.name ?? '';
    _username = (userinfo.username as string) ?? '';

    let latestRefreshToken = tokens.refresh_token ?? '';

    if (isMapFavoritesResourceServerEnabled()) {
      const resourceTokens = await oc.refreshTokenGrant(
        config,
        tokens.refresh_token ?? '',
        {
          resource: getResourceIndicator(),
          scope: getOidcScope(),
        }
      );

      const resourceAccessToken = resourceTokens.access_token ?? '';
      if (resourceAccessToken === '') {
        throw new Error('Failed to obtain resource access token.');
      }

      sessionStorage.setItem(RESOURCE_ACCESS_TOKEN_KEY, resourceAccessToken);
      latestRefreshToken = getLatestRefreshToken(
        resourceTokens.refresh_token,
        tokens.refresh_token ?? ''
      );
    } else {
      sessionStorage.removeItem(RESOURCE_ACCESS_TOKEN_KEY);
    }

    // アプリ全体では従来どおり UserInfo 用 access token を保持する
    _expiresInSeconds = getCurrentUnixTimestamp() + (tokens.expires_in ?? 0);

    return {
      accessToken: tokens.access_token,
      refreshToken: latestRefreshToken,
      userId: _userId,
      name: _name,
      username: _username,
    };
  } catch (reason) {
    console.error(reason);
  }

  return null;
};

export const openSignInPage = async () => {
  const redirectUri = getRedirectUri();
  const singninBackPath = window.location.pathname + window.location.search;
  const issuer = new URL(
    REACT_APP_CUCON_OIDC_DOMAIN + CUCON_OIDC_API_PATH.issuer
  );
  const clientId = jsonServiceProfile?.cuconConfig?.clientId ?? '';
  const config = await oc.discovery(issuer, clientId);

  const codeVerifier = oc.randomPKCECodeVerifier();
  const codeChallenge = await oc.calculatePKCECodeChallenge(codeVerifier);
  const state = oc.randomState();
  const nonce = oc.randomNonce();
  sessionStorage.setItem(PKCE_VERIFIER_KEY, codeVerifier);
  sessionStorage.setItem(STATE_KEY, state);
  sessionStorage.setItem(NONCE_KEY, nonce);
  sessionStorage.setItem(SIGNIN_BACK_PATH, singninBackPath);

  const params = new URLSearchParams([
    ['client_id', clientId],
    ['redirect_uri', redirectUri.toString()],
    ['response_type', 'code'],
    ['state', state],
    ['scope', getOidcScope()],
    ['nonce', nonce],
    ['code_challenge', codeChallenge],
    ['code_challenge_method', 'S256'],
  ]);

  if (isMapFavoritesResourceServerEnabled()) {
    params.set('resource', getResourceIndicator());
  }

  const authUrl = oc.buildAuthorizationUrl(config, params);
  window.location.href = authUrl.toString();

  return;
};

export const openSignOutPage = async () => {
  const issuer = new URL(
    REACT_APP_CUCON_OIDC_DOMAIN + CUCON_OIDC_API_PATH.issuer
  );
  const clientId = jsonServiceProfile?.cuconConfig?.clientId ?? '';
  const postLogoutRedirectUri = new URL(window.location.origin);
  const config = await oc.discovery(issuer, clientId);
  const idTokenHint = sessionStorage.getItem(ID_TOKEN_KEY);
  const params = new URLSearchParams([
    ['post_logout_redirect_uri', postLogoutRedirectUri.toString()],
  ]);

  if (idTokenHint != null && idTokenHint !== '') {
    params.set('id_token_hint', idTokenHint);
  }

  sessionStorage.removeItem(RESOURCE_ACCESS_TOKEN_KEY);
  sessionStorage.removeItem(ID_TOKEN_KEY);
  sessionStorage.removeItem(PKCE_VERIFIER_KEY);
  sessionStorage.removeItem(STATE_KEY);
  sessionStorage.removeItem(NONCE_KEY);
  sessionStorage.removeItem(SIGNIN_BACK_PATH);
  _expiresInSeconds = 0;
  _userId = '';
  _name = '';
  _username = '';

  const logoutUrl = oc.buildEndSessionUrl(config, params);
  window.location.href = logoutUrl.toString();
};
